define((require) => {
    "use strict";

    const {
        defer, debounce, each, extend
    } = require("underscore");
    const $                               = require("jquery");
    const Dom                             = require("channels/Dom");
    const ExperienceModel                 = require("shared/ui/experience/ExperienceModel");
    const EditBehavior                    = require("ui/behaviors/EditBehavior");
    const EditorEvents                    = require("shared/services/EditorEvents");
    const ImprovedView                    = require("editor/zbone/ImprovedView");
    const PublishUtil                     = require("ui/publish/PublishUtil");
    const {isBlank}                       = require("shared/util/StringUtils");
    const template                        = require("ui/publish/channelSetup/ChannelPropertiesView.tpl");
    const ValidateBehavior                = require("ui/behaviors/ValidateBehavior");
    const ChannelPropertiesViewController = require("ui/publish/channelSetup/ChannelPropertiesViewController");

    /**
     * View of the properties of the channel.
     *
     * @author Poul Henriksen
     * @author Antonio Lagrotteria
     */
    return ImprovedView.extend({

        template,

        className: "channel-properties-view",

        controller: ChannelPropertiesViewController,

        templateHelpers() {
            return {
                notAllowedToPublish: !this.options.allowedToPublish,
                slotName: this.model.get("name")
            };
        },

        ui: {
            nameInput: ".name",
            calendarIcon: ".glyphicon-calendar",
            startTime: ".next-start-time",
            liveSelect: ".live-select",
            nextSelect: ".next-select",
            startTimePicker: ".next-start-time-picker",
            emptyScheduleDate: ".empty-schedule-date",
            okButton: ".btn-ok",
            cancelButton: ".btn-cancel"
        },

        bindings: {
            ".selector": "selector",
            ".url": "url",
            ".slot-schedule-notification": {
                observe: "schedules",
                updateView: false,
                visible(value) {
                    const scheduledExperience = value ? value[0] : undefined;

                    return scheduledExperience && scheduledExperience.takeDownCurrent;
                }
            },

            ".live-select": {
                observe: ["experience", "group"],

                onSet() {
                    const data = this.ui.liveSelect.select2("data");
                    const model = data.id;

                    if (!model) {
                        return [null, null];
                    }

                    if (model instanceof ExperienceModel) {
                        return [model, null];
                    }

                    // Must be group...
                    return [null, model];
                }
            },

            ".next-select": {
                observe: "schedules",

                onGet: function(schedules) {
                    const schedule = schedules ? schedules[0] : null;

                    if (schedule) {
                        if (schedule.takeDownCurrent) {
                            return PublishUtil.NO_EXPERIENCE_SCHEDULED;
                        }

                        return schedule.publishable;
                    }

                    return null;
                },


                onSet() {
                    const data = this.ui.nextSelect.select2("data");
                    const publishableModel = data.id;
                    if (publishableModel) {
                        this._enableStartTimePicker(true);

                        const schedule = this.model.getFirstSchedule();

                        if (schedule) {
                            if (this._isNoExperienceScheduled(publishableModel)) {
                                schedule.takeDownCurrent = true;
                                schedule.experience = null;
                                schedule.group = null;
                                schedule.publishable = null;
                                return [schedule];
                            }

                            if (publishableModel instanceof ExperienceModel) {
                                schedule.experience = publishableModel;
                                schedule.group = null;
                            } else {
                                schedule.group = publishableModel;
                                schedule.experience = null;
                            }
                            schedule.publishable = publishableModel;
                            schedule.takeDownCurrent = false;
                            return [schedule];
                        }


                        if (this._isNoExperienceScheduled(publishableModel)) {
                            return [{takeDownCurrent: true}];
                        }

                        return publishableModel instanceof ExperienceModel ? [{experience: publishableModel}] : [{group: publishableModel}];
                    }

                    this._enableStartTimePicker(false);
                    return [];
                }
            }
        },

        behaviors: {
            editBehavior: {
                behaviorClass: EditBehavior,
                container: ".editable-container",
                editFields: [{
                    input: ".name",
                    save(val) {
                        this.model.set("name", val);
                    }
                }]
            },
            validateBehavior: {
                behaviorClass: ValidateBehavior,

                buttonSubmit: ".btn-ok",

                inputValidation: {
                    ".next-start-time": {
                        validator() {
                            if (isBlank(this.ui.nextSelect.val())) {
                                return true;
                            }
                            return !isBlank(this.ui.startTime.val());
                        },

                        errorText() {
                            return "Schedule date required";
                        }
                    },

                    ".selector": {
                        validator(selector) {
                            // Empty selector allowed
                            if (!isBlank(selector)) {
                                // Try and select using the entered selector; if it succeeds silently, we are ok, but
                                // it will raise an exception when invalid...
                                try {
                                    Dom.selectElements(selector);
                                } catch (e) {
                                    return false;
                                }
                            }
                            return true;
                        },

                        errorText() {
                            return "Not a valid CSS selector";
                        }
                    }
                }
            }
        },

        events: {
            "click @ui.okButton": "_okClickHandler",
            "click @ui.cancelButton": "close",
            "click @ui.startTime": "_startTimeClickHandler"
        },

        onRender() {
            // Set up the time picker
            this.ui.startTimePicker.datetimepicker({
                pickSeconds: false,
                startDate: new Date()
            });

            const picker = this.ui.startTimePicker.data("datetimepicker");
            const schedule = this.model.getFirstSchedule();

            if (schedule && schedule.startTime) {
                picker.setLocalDate(new Date(schedule.startTime));
            }

            this._enableStartTimePicker(!!schedule);
            this.ui.startTimePicker.on("changeDate", (e) => {
                if (e.localDate) {
                    this.model.getFirstSchedule().startTime = e.localDate.getTime();
                }
            });
        },

        onShow() {
            const {liveSelect, nextSelect} = this.ui;
            const {
                allowedToPublish, focus, selectedLiveModel, selectedScheduledModel
            } = this.options;

            liveSelect.select2(this._buildSelectSettings(selectedLiveModel));
            nextSelect.select2(this._buildSelectSettings(selectedScheduledModel, true));

            if (!allowedToPublish) {
                return;
            }

            this._handleFocus(focus);
        },

        _isNoExperienceScheduled(model) {
            return model && model === PublishUtil.NO_EXPERIENCE_SCHEDULED;
        },

        _handleFocus(focus) {
            const {
                liveSelect, nextSelect, nameInput, startTime
            } = this.ui;

            // Somebody with CSS skills could probably make this *much* nicer...
            let focusFn = null;

            switch (focus) {
                case "name":
                    focusFn = () => { nameInput.trigger("mousedown"); };
                    break;

                case "experience":
                    focusFn = () => { liveSelect.select2("open"); };
                    break;

                case "schedules":
                    focusFn = () => { nextSelect.select2("open"); };
                    break;

                case "schedules.startTime":
                    focusFn = () =>{
                        startTime.trigger("focus");
                        this._startTimeClickHandler();
                    };
                    break;

                default: // No need to log here.
                    break;
            }

            if (focusFn) {
                defer(focusFn.bind(this));
            }
        },

        _buildSelectSettings(initialModel, schedule) {
            const {projectContext} = this.options;
            const project = projectContext && projectContext.project;

            return {
                initSelection: (element, callback) => {
                    this.trigger("initSelection", callback, initialModel);
                },

                query: debounce((options) => {
                    this.trigger("fetchResults", options, project, schedule);
                }, 600)
            };
        },

        _okClickHandler() {
            if (!this.inputValidation()) {
                return;
            }

            this.trigger("done", this.model);
            this.close();

            this._triggerEditorEvents();
        },

        /**
         * Sends Mix Panel event for the channel.
         * @private
         */
        _triggerEditorEvents() {
            const {isNewSlot, projectContext} = this.options;
            let eventName = EditorEvents.constants.ADD_CHANNEL;
            let triggerOrigin = "CreateNewChannelSlotModal";

            if (!isNewSlot) {
                eventName = EditorEvents.constants.UPDATE_CHANNEL;
                triggerOrigin = "ExistingChannelSlotModal";
            }

            const projectId = projectContext && projectContext.project.get("id");

            const eventProperties = extend(PublishUtil.buildEventProperties({
                channel: this.model,
                triggerOrigin,
                dragAndDrop: false,
                manual: true,
                projectId
            }), {
                isNewSlot
            });

            EditorEvents.trigger(eventName, eventProperties);
        },

        _startTimeClickHandler() {
            if (this.options.allowedToPublish) {
                // Trigger datetimepicker on input field
                this.ui.calendarIcon.trigger("click");
            }
        },

        _enableStartTimePicker(enabled) {
            const {emptyScheduleDate, startTimePicker} = this.ui;
            if (enabled) {
                emptyScheduleDate.css("visibility", "hidden");
                startTimePicker.css("visibility", "visible");
            } else {
                emptyScheduleDate.css("visibility", "visible");
                startTimePicker.css("visibility", "hidden");
            }
        },

        /**
         * Select2 has a problem when trying to dynamically add nested options under an existing opt group ("All" OR "This Project"), by creating a new one opt group, instead of prepending options to the existing one.
         * (E.g: if "All" category has already options, and the collection has more results, the new options will not be rendered within the "All" opt group, but they will be prepended to a new one)
         *
         * This method removes the opt group for a new set of results, by removing it from the DOM.
         *
         */
        removeEmptyOptGroups() {
            const labels = $(".select2-results .select2-result-label");
            each(labels, (label) => {
                if ($(label).text().indexOf(PublishUtil.PENDING_OPT_GROUP_RESULT) !== -1) {
                    $(label).remove();
                }
            });
        },

        onClose() {
            this.ui.startTimePicker.datetimepicker("destroy");
            this.ui.liveSelect.select2("destroy");
            this.ui.nextSelect.select2("destroy");

            if (this.options.parentView) {
                this.options.parentView._isLightboxOpening = false;
            }
        }
    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/publish/channelSetup/ChannelPropertiesView.js